import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { updateEntities, deleteEntity } from '@redux/entities'

import api from '@functions/api'

import type { PriceModel } from '@models/price'
import type { AppDispatch } from '@redux/store'
import type {
  Action, ActionResult, EntityOptions, Errors, FetchRequestPayload, RootReducerState,
} from '@redux/modules/types'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/price/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/price/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/price/FETCH_FAILURE'

const CREATE_REQUEST = 'realbase/price/CREATE_REQUEST'
const CREATE_SUCCESS = 'realbase/price/CREATE_SUCCESS'
const CREATE_FAILURE = 'realbase/price/CREATE_FAILURE'

const UPDATE_REQUEST = 'realbase/price/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realbase/price/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realbase/price/UPDATE_FAILURE'

const DELETE_REQUEST = 'realbase/price/DELETE_REQUEST'
const DELETE_SUCCESS = 'realbase/price/DELETE_SUCCESS'
const DELETE_FAILURE = 'realbase/price/DELETE_FAILURE'

export type PriceModuleState = {
  creating: boolean,
  deleting: boolean,
  errors: string[],
  loaded: boolean,
  loadedForKeys: string[],
  loadedIds: number[],
  loading: boolean,
  updating: boolean,
}

const initialState: PriceModuleState = {
  creating: false,
  deleting: false,
  errors: [],
  loaded: false,
  loadedForKeys: [],
  loadedIds: [],
  loading: false,
  updating: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}): Action {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
    loadedIds: payload.loadedIds,
  }
}

export function fetchSuccess(): Action {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors: Errors = []): Action {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest(): Action {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess(): Action {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors: Errors = []): Action {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest(): Action {
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess(): Action {
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors: Errors = []): Action {
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest(): Action {
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess(): Action {
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors: Errors = []): Action {
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadPrices(options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState): Promise<ActionResult<PriceModel[]>> => {
    const loadedForKeys = [...getState().prices.loadedForKeys]

    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/prices', options)
      .then(({ data }) => {
        const normalizedJson = normalize(data, Schemas.PRICE_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false, errors }
      })

    return promise
  }
}

export function loadPrice(priceId: number, options: EntityOptions = {}) {
  return (dispatch: AppDispatch, getState: () => RootReducerState): Promise<ActionResult<PriceModel>> => {
    const state = getState()
    const loadedIds = [...state.prices.loadedIds]

    if (priceId && !loadedIds.includes(priceId)){
      loadedIds.push(priceId)
    }

    dispatch(fetchRequest({ loadedIds }))

    const promise = api(`/prices/${priceId}`, options)
      .then(({ data }) => {
        const normalizedJson = normalize(data, Schemas.PRICE)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))
        return { success: false, errors }
      })

    return promise
  }
}

export function createPrice(price: PriceModel, options: EntityOptions = {}) {
  const config = {
    method: 'POST',
    data: JSON.stringify(price),
  }

  return (dispatch: AppDispatch): Promise<ActionResult<PriceModel>> => {
    dispatch(createRequest())

    const promise = api('/prices', options, config)
      .then(({ data }) => {
        const normalizedJson = normalize(data, Schemas.PRICE)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false, errors }
      })

    return promise
  }
}

export function updatePrice(price: PriceModel, options: EntityOptions = {}) {
  const config = {
    method: 'PUT',
    data: JSON.stringify(price),
  }

  return (dispatch: AppDispatch): Promise<ActionResult<PriceModel>> => {
    dispatch(updateRequest())

    const promise = api(`/prices/${price.id}`, options, config)
      .then(({ data }) => {
        const normalizedJson = normalize(data, Schemas.PRICE)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false, errors }
      })

    return promise
  }
}

export function deletePrice(price: PriceModel) {
  const config = {
    method: 'DELETE',
  }

  return (dispatch: AppDispatch): Promise<ActionResult<PriceModel>> => {
    dispatch(deleteRequest())

    const promise = api(`/prices/${price.id}`, {}, config)
      .then(({ data }) => {
        const normalizedJson = normalize(data, Schemas.PRICE)
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess())

        return { success: true, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(deleteFailure(errors))

        return { success: false, errors }
      })
    return promise
  }
}

// Reducer
export default function reducer(
  state: PriceModuleState = initialState,
  action: Action = { type: '' },
): PriceModuleState {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
        loadedIds: action.loadedIds || state.loadedIds,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors || [],
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors || [],
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors || [],
      }
    case DELETE_REQUEST:
      return {
        ...state,
        deleting: true,
      }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        errors: [],
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
